import {
  buildContext,
  computeRecipeCostPartial,
  estimatePrice,
  grossFromNet,
  netFromGross,
  type Ingredient,
  type PriceEstimate,
  type Recipe,
  type RecipeCost,
  type Unit,
} from '@alexiarest/shared';

/**
 * La calculadora publica de escandallo (/costeo, 23 sep 2026): el motor de
 * costes de shared corriendo en el navegador, sin cuenta ni backend. Los
 * precios salen de la tabla de estimados de Espanya salvo que el usuario los
 * ajuste. El plato se guarda en localStorage y se siembra en la cuenta tras
 * el alta (OnboardingPage), etiquetada con signupCampaign 'costeo-express'.
 */

/** Unidades de la calculadora: todas comprables y con dimension en la tabla de estimados. */
export const UNIDADES_COSTEO = ['g', 'kg', 'ml', 'l', 'pz'] as const satisfies readonly Unit[];
export type UnidadCosteo = (typeof UNIDADES_COSTEO)[number];

export interface CosteoLinea {
  name: string;
  qty: number;
  unit: UnidadCosteo;
  /** Precio por 1 `unit`, sin IVA, solo si el usuario ajusto el estimado. */
  netPrice?: number;
}

export interface CosteoInput {
  name: string;
  /** PVP con IVA. 0 = aun sin decidir (se ensenya solo el coste). */
  sellingPriceGross: number;
  vatPct: number;
  lines: CosteoLinea[];
}

export const TARGET_FOOD_COST_PCT = 30;

export interface CosteoResultado {
  cost: RecipeCost;
  /** Ingreso sin IVA del PVP dado (0 si no hay PVP). */
  netRevenue: number;
  /** Margen bruto por plato (solo con PVP). */
  margin: number | null;
  foodCostPct: number | null;
  /** PVP (con IVA) que dejaria el food cost en el objetivo. */
  suggestedPriceGross: number | null;
  /** Lineas sin precio conocido ni estimable: el coste mostrado es un minimo. */
  pendingLines: number;
}

export const estimacionDeLinea = (name: string, unit: UnidadCosteo): PriceEstimate | null =>
  name.trim() ? estimatePrice(name.trim(), unit, 'ES') : null;

export function calcularCosteo(input: CosteoInput): CosteoResultado | null {
  const lines = input.lines.filter((l) => l.name.trim() && l.qty > 0);
  if (!lines.length) return null;
  const ingredients = lines.map((l, i) => ({
    tenantId: 'costeo',
    ingredientId: `linea-${i}`,
    name: l.name.trim(),
    group: 'otros',
    vatPct: 10,
    active: true,
    ...(l.netPrice != null && l.netPrice > 0
      ? { netPrice: l.netPrice, purchaseQty: 1, purchaseUnit: l.unit }
      : { priceEstimated: estimacionDeLinea(l.name, l.unit) ?? undefined }),
  })) as unknown as Ingredient[];
  const recipe = {
    tenantId: 'costeo',
    recipeId: 'plato',
    name: input.name.trim() || 'Tu plato',
    kind: 'plato',
    active: true,
    yieldQty: 1,
    yieldUnit: 'pz',
    portions: 1,
    // Sin margen de seguridad: la calculadora ensenya el numero desnudo.
    safetyMarginPct: 0,
    useYieldCostAsBase: false,
    components: lines.map((l, i) => ({
      ref: { kind: 'ingredient', ingredientId: `linea-${i}` },
      block: 'ingrediente',
      qty: l.qty,
      unit: l.unit,
      wastePct: 0,
    })),
  } as unknown as Recipe;
  const cost = computeRecipeCostPartial('plato', buildContext(ingredients, [recipe]));
  const pendingLines = cost.lines.filter((l) => l.pending).length;
  const pvp = input.sellingPriceGross;
  const netRevenue = pvp > 0 ? netFromGross(pvp, input.vatPct) : 0;
  return {
    cost,
    netRevenue,
    margin: pvp > 0 ? netRevenue - cost.costPerPortion : null,
    foodCostPct: pvp > 0 && netRevenue > 0 ? (cost.costPerPortion / netRevenue) * 100 : null,
    suggestedPriceGross:
      cost.costPerPortion > 0 ? grossFromNet(cost.costPerPortion / (TARGET_FOOD_COST_PCT / 100), input.vatPct) : null,
    pendingLines,
  };
}

/* ------------------------------------------------------------------ */
/* El plato pendiente de sembrar tras el alta                          */
/* ------------------------------------------------------------------ */

const KEY = 'alexia:costeo-pendiente';
const TTL_MS = 30 * 86_400_000;

export function guardarCosteoPendiente(input: CosteoInput): void {
  try { localStorage.setItem(KEY, JSON.stringify({ ...input, savedAt: Date.now() })); } catch { /* privado o lleno: sin siembra */ }
}

export function costeoPendiente(): CosteoInput | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CosteoInput & { savedAt?: number };
    if (typeof parsed?.savedAt !== 'number' || Date.now() - parsed.savedAt > TTL_MS) {
      localStorage.removeItem(KEY);
      return null;
    }
    if (typeof parsed.name !== 'string' || !Array.isArray(parsed.lines)) return null;
    return parsed;
  } catch { return null; }
}

export function clearCosteoPendiente(): void {
  try { localStorage.removeItem(KEY); } catch { /* nada que limpiar */ }
}
